// src/components/home/FeaturedCars.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../../styles/home/FeaturedCars.css";
import { fetchPageOfCars } from "../../services/car";
import { shortlistService } from "../../services/shortlist";
import { useAuth } from "../../context/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getImageUrl(car) {
  const raw = car?.images_urls?.[0] || car?.image_url || null;
  if (!raw) return null;
  if (raw.startsWith("http")) return raw;
  return `${API_BASE}${raw}`;
}

function formatPrice(price) {
  if (!price) return "Price on request";
  const lakh = price / 100000;
  return `₹${lakh % 1 === 0 ? lakh.toFixed(0) : lakh.toFixed(2)} Lakh`;
}

function formatKm(km) {
  if (!km) return null;
  const num = Number(km);
  if (num >= 1000) return `${Math.round(num / 1000)}K km`;
  return `${num} km`;
}

function calcEMI(price) {
  const p = parseFloat(price);
  if (!p || p <= 0) return null;
  const r = 8.5 / 12 / 100;
  const n = 60;
  const emi = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  return Math.round(emi);
}

function extractCars(res) {
  if (!res) return [];
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
}

// ── Heart / Shortlist Button ──────────────────────────────────────────────────

function HeartButton({ carId, isShortlisted, onToggle }) {
  return (
    <button
      className={`fc-heart${isShortlisted ? " fc-heart--active" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(carId);
      }}
      aria-label={isShortlisted ? "Remove from shortlist" : "Add to shortlist"}
    >
      <svg
        viewBox="0 0 24 24"
        fill={isShortlisted ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    </button>
  );
}

// ── Car Card ──────────────────────────────────────────────────────────────────

function CarCard({ car, shortlistedIds, onToggleShortlist }) {
  const navigate = useNavigate();
  const imgUrl = getImageUrl(car);
  const emi = calcEMI(car.price);
  // Normalise to string for reliable comparison
  const isShortlisted = shortlistedIds.has(String(car.id));

  return (
    <div className="fc-card" onClick={() => navigate(`/car/${car.id}`)}>
      <div className="fc-img-wrap">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={car.title || `${car.brand} ${car.model}`}
            className="fc-img"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
              if (e.target.nextElementSibling)
                e.target.nextElementSibling.style.display = "flex";
            }}
          />
        ) : null}
        <div
          className="fc-img-placeholder"
          style={{ display: imgUrl ? "none" : "flex" }}
        >
          <span>🚗</span>
        </div>
      </div>

      <div className="fc-body">
        <div className="fc-title-row">
          <h4 className="fc-title">
            {car.title || `${car.year} ${car.brand} ${car.model}`}
          </h4>
          <HeartButton
            carId={car.id}
            isShortlisted={isShortlisted}
            onToggle={onToggleShortlist}
          />
        </div>

        <div className="fc-specs">
          {car.km_driven && <span>{formatKm(car.km_driven)}</span>}
          {car.km_driven && car.fuel_type && <span className="fc-dot">·</span>}
          {car.fuel_type && (
            <span>
              {car.fuel_type.charAt(0).toUpperCase() + car.fuel_type.slice(1)}
            </span>
          )}
          {car.fuel_type && car.transmission && <span className="fc-dot">·</span>}
          {car.transmission && (
            <span>
              {car.transmission.charAt(0).toUpperCase() + car.transmission.slice(1)}
            </span>
          )}
        </div>

        <div className="fc-price-row">
          <span className="fc-price">{formatPrice(car.price)}</span>
          {emi && (
            <span className="fc-emi">
              EMI from ₹{emi.toLocaleString("en-IN")}/m
            </span>
          )}
        </div>

        {car.location && (
          <div className="fc-location">
            <span className="fc-location-dot" />
            {car.location}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Skeleton Card ─────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="fc-card fc-skeleton">
      <div className="fc-skel-img" />
      <div className="fc-skel-body">
        <div className="fc-skel-line fc-skel-long" />
        <div className="fc-skel-line fc-skel-medium" />
        <div className="fc-skel-line fc-skel-short" />
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const FeaturedCars = () => {
  const [activeTab, setActiveTab] = useState("best");
  const [bestCars, setBestCars] = useState([]);
  const [newCars, setNewCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [shortlistedIds, setShortlistedIds] = useState(new Set());

  // ✅ THE FIX: a ref that always holds the CURRENT set value.
  // handleToggleShortlist reads from this ref instead of capturing
  // shortlistedIds in its closure — so it never goes stale and never
  // overwrites the optimistic update with an old value.
  const shortlistedIdsRef = useRef(new Set());

  const { user } = useAuth();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  // Keep ref in sync whenever state updates
  useEffect(() => {
    shortlistedIdsRef.current = shortlistedIds;
  }, [shortlistedIds]);

  // Load which cars the logged-in user has already shortlisted
  useEffect(() => {
    if (!user) {
      const empty = new Set();
      setShortlistedIds(empty);
      shortlistedIdsRef.current = empty;
      return;
    }
    shortlistService.getShortlistedCarIds()
      .then((ids) => {
        const s = new Set(ids.map(String));
        setShortlistedIds(s);
        shortlistedIdsRef.current = s;
      })
      .catch(() => {});
  }, [user]);

  // ✅ No `shortlistedIds` in deps — reads live value from ref instead.
  // This is the only change needed: removing shortlistedIds from the dep array
  // (and replacing the stale closure read with a ref read) stops React from
  // recreating this function on every toggle, which was causing the parent to
  // re-render and briefly flash the un-toggled state.
  const handleToggleShortlist = useCallback(async (carId) => {
    if (!user) {
      navigate("/login");
      return;
    }

    const id = String(carId);
    const wasShortlisted = shortlistedIdsRef.current.has(id);

    // Optimistic: update state AND ref together so the next click
    // immediately sees the correct "current" value
    setShortlistedIds((prev) => {
      const next = new Set(prev);
      wasShortlisted ? next.delete(id) : next.add(id);
      shortlistedIdsRef.current = next; // ← keep ref in sync
      return next;
    });

    try {
      if (wasShortlisted) {
        await shortlistService.removeFromShortlist(carId);
      } else {
        await shortlistService.addToShortlist(carId);
      }
    } catch (_) {
      // Revert on API failure
      setShortlistedIds((prev) => {
        const next = new Set(prev);
        wasShortlisted ? next.add(id) : next.delete(id);
        shortlistedIdsRef.current = next;
        return next;
      });
    }
  }, [user, navigate]); // ✅ shortlistedIds intentionally excluded — read via ref

  // Fetch cars (both tabs in parallel — safe because fetchPageOfCars
  // creates its own AbortController per call)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [bestRes, newRes] = await Promise.all([
          fetchPageOfCars(1, 10),
          fetchPageOfCars(2, 10),
        ]);
        if (cancelled) return;
        const best = extractCars(bestRes);
        let newly = extractCars(newRes);
        if (newly.length === 0 && best.length > 0) newly = [...best].reverse();
        setBestCars(best);
        setNewCars(newly);
      } catch (err) {
        if (!cancelled) { setBestCars([]); setNewCars([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const activeCars = activeTab === "best" ? bestCars : newCars;

  // Scroll helpers
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const t = setTimeout(updateScrollState, 120);
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      clearTimeout(t);
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [activeCars, updateScrollState]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    setTimeout(updateScrollState, 150);
  }, [activeTab, updateScrollState]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector(".fc-card");
    const amount = card ? card.offsetWidth + 16 : 290;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section className="fc-section">
      <h2 className="fc-heading">Featured CarYugo Cars</h2>

      <div className="fc-tabs">
        <button
          className={`fc-tab${activeTab === "best" ? " fc-tab--active" : ""}`}
          onClick={() => setActiveTab("best")}
        >
          Best buys for you
        </button>
        <button
          className={`fc-tab${activeTab === "new" ? " fc-tab--active" : ""}`}
          onClick={() => setActiveTab("new")}
        >
          Newly added
        </button>
      </div>

      {loading ? (
        <div className="fc-outer">
          <div className="fc-clip">
            <div className="fc-track">
              {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </div>
        </div>
      ) : activeCars.length === 0 ? (
        <p className="fc-empty">No cars available at the moment.</p>
      ) : (
        <div className="fc-outer">
          <button
            className={`fc-arrow fc-arrow--left${!canScrollLeft ? " fc-arrow--disabled" : ""}`}
            onClick={() => scroll(-1)}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="fc-clip">
            <div className="fc-track" ref={scrollRef}>
              {activeCars.map((car) => (
                <CarCard
                  key={car.id}
                  car={car}
                  shortlistedIds={shortlistedIds}
                  onToggleShortlist={handleToggleShortlist}
                />
              ))}
            </div>
          </div>

          <button
            className={`fc-arrow fc-arrow--right${!canScrollRight ? " fc-arrow--disabled" : ""}`}
            onClick={() => scroll(1)}
            disabled={!canScrollRight}
            aria-label="Scroll right"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}

      <div className="fc-view-all-wrap">
        <Link to="/buy-cars" className="fc-view-all-btn">
          View all CarYugo cars
        </Link>
      </div>
    </section>
  );
};

export default FeaturedCars;